// Typed client + query hooks for the storefront module's admin API (STORE/THEME/CONTENT; handlers in
// packages/domain/store/src/http.ts). Every key carries the org and store IDs.
import { useQuery } from "@tanstack/react-query";
import { API_BASE, request } from "../../api/client";
import type { Page } from "../../api/types";

const S = (orgId: string, storeId: string) =>
  `/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}`;

export type StoreStatus = "draft" | "coming_soon" | "password" | "live" | "suspended";

export interface StoreSummary {
  id: string;
  name: string;
  slug: string;
  status: StoreStatus;
  canonical_host: string;
  content_version: number;
  hosts: { hostname: string; kind: string; canonical: boolean; serve_mode: string }[];
  version: number;
  warnings?: { code: string; missing?: string[] }[];
  /** D-094: the "your shop is live" celebration has not been shown yet. */
  celebrate?: boolean;
}

export interface BrandTokens {
  primary: string;
  accent: string;
  radius: "sm" | "md" | "lg";
  currency_label: "Rs" | "रु";
  image_ratio: "square" | "portrait" | "landscape";
  logo?: { asset_id: string; alt: string };
  /** D-070: the shop's tab icon; the storefront falls back to the logo. */
  favicon?: { asset_id: string; alt: string };
}

/** A section as the editor holds it (the server schema validates on save). */
export interface SectionDoc {
  id: string;
  type: string;
  hidden?: boolean;
  settings: Record<string, unknown>;
}

/** D-074: built-in store pages whose sections the theme editor can arrange. */
export const TEMPLATE_KEYS = [
  "product",
  "listing",
  "search",
  "cart",
  "account",
  "track",
  "thanks",
  "blog",
  "blog_post",
  "contact",
  "not_found",
] as const;
export type TemplateKey = (typeof TEMPLATE_KEYS)[number];

/** D-091: page layouts the storefront knows (one per first-party theme). */
export type ThemeLayout =
  | "mega"
  | "trend"
  | "shadow"
  | "elegant"
  | "classic"
  // D-092 signature themes.
  | "lumen"
  | "volt"
  | "folio"
  | "pop"
  | "pantry"
  // D-114: the platform default.
  | "default";

export interface ThemeDocument {
  /** D-091: the page layout; absent (older documents) = the catalogue theme's layout. */
  layout?: ThemeLayout;
  /** D-093: a font pairing from the library (over the layout's) and the theme's own stylesheet. */
  font?: string;
  theme_css?: string;
  tokens: BrandTokens;
  /** custom_css: the merchant's stylesheet from the Code editor (D-049, checked on save). */
  settings: {
    announcement?: string;
    show_search: boolean;
    footer_note?: string;
    custom_css?: string;
    /** D-091: the big menu under main-menu items that have children. */
    mega_menu?: {
      enabled: boolean;
      heading?: string;
      promo_image?: { asset_id: string; alt: string };
      promo_href?: string;
    };
  };
  home: SectionDoc[];
  /** D-074: sections around each built-in page's own content (`main`); absent = content alone. */
  templates?: Partial<Record<TemplateKey, SectionDoc[]>>;
}

export interface ThemeDraft {
  install_id: string | null;
  theme_key: string | null;
  theme_version?: string;
  document: ThemeDocument | null;
  draft_updated_at?: string | null;
  live: boolean;
  published_n?: number | null;
  version?: number;
}

export interface CatalogTheme {
  id: string;
  key: string;
  name: string;
  version: string;
  layout: ThemeLayout;
  summary: string;
  presets: { primary: string; accent: string; radius: string };
  /** D-091: "uploaded" = a theme the owner uploaded in the console (not built into the code). */
  source?: "built_in" | "uploaded";
  /** D-091: the uploaded theme's card picture; built-in themes are drawn instead. */
  preview_url?: string | null;
  /** D-093: an uploaded theme whose package has a preview block (photos, sample products):
   * `sfApi.previewData` draws it live. */
  has_preview?: boolean;
}

/** D-093: GET /themes/:key/preview-data — a published uploaded theme's document and preview block. */
export interface ThemePreviewData {
  version: string;
  document: ThemeDocument;
  preview: {
    card?: string;
    images?: Record<string, string>;
    products?: { title: string; price_paisa: number; compare_at_paisa?: number; image?: string }[];
  };
}

/** D-093: the address of a published uploaded theme's file (`images/…`) at a version. */
export const themeFileUrl = (key: string, version: string, path: string): string =>
  `${API_BASE}/themes/${encodeURIComponent(key)}/files/${path
    .split("/")
    .map(encodeURIComponent)
    .join("/")}?v=${encodeURIComponent(version)}`;

export interface ThemeVersion {
  id: string;
  n: number;
  label: string | null;
  named: boolean;
  theme_key: string;
  published_by: string;
  published_at: string;
  live: boolean;
}

export interface LegalAddress {
  province?: string;
  district?: string;
  local_level?: string;
  ward?: number;
  tole?: string;
}

export interface LegalProfile {
  registered_name: string | null;
  business_type: string | null;
  pan_vat: string | null;
  registration_no: string | null;
  registration_body: string | null;
  ecommerce_listing_no: string | null;
  complaint_officer_name: string | null;
  complaint_officer_contact: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  address: LegalAddress;
  branches: { name: string; address: string }[];
  completeness: number;
  missing: string[];
  version: number;
  updated_at: string | null;
}

export type SocialNetwork = "facebook" | "instagram" | "tiktok" | "youtube" | "x";
export const STORE_CATEGORIES = [
  "fashion",
  "electronics",
  "beauty",
  "grocery",
  "home",
  "books",
  "sports",
  "kids",
  "handicrafts",
  "health",
  "food",
  "services",
  "other",
] as const;

export interface StoreSettings {
  /** The store name (control row stores.name), editable since 2026-10-22. */
  name?: string;
  contact: { phone?: string; whatsapp?: string; email?: string };
  /** Social profile links shown in the store footer (null = not set). */
  social?: Partial<Record<SocialNetwork, string | null>>;
  category?: (typeof STORE_CATEGORIES)[number] | null;
  /** Home page search text, the share photo and the Google Search Console code (STORE-001). */
  seo: {
    title?: string;
    description?: string;
    image?: { asset_id: string; alt: string };
    google_verification?: string;
    /** D-071: Bing Webmaster Tools code (msvalidate.01). */
    bing_verification?: string;
    /** D-071: which AI crawlers may read the store (robots.txt); absent = all. */
    ai_crawlers?: "all" | "search" | "none";
  };
  gate_message: { title?: string; body?: string };
  stock_display: "state" | "count";
  has_password: boolean;
  version: number;
}

export interface ReadinessItem {
  key: string;
  state: "done" | "todo" | "unknown";
  fix: string;
  details?: Record<string, unknown>;
}

export interface ContentEntry {
  id: string;
  type: string;
  key: string;
  title: string;
  body: string;
  position: number;
  data: Record<string, unknown>;
  template_state: "draft_review_required" | "reviewed";
  placeholders: string[];
  live: boolean;
  version: number;
  updated_at: string | null;
}

export interface StorePage {
  id: string;
  kind: "home" | "landing" | "custom";
  slug: string;
  title: string;
  seo: { title?: string; description?: string };
  sections: SectionDoc[];
  published: boolean;
  has_unpublished_changes: boolean;
  published_at: string | null;
  is_home: boolean;
  version: number;
  updated_at: string | null;
}

export interface MenuItem {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

export interface Submission {
  id: string;
  kind: "contact" | "grievance";
  reference_no: string;
  data: Record<string, unknown>;
  contact_phone: string | null;
  contact_email: string | null;
  pii_masked: boolean;
  pipeline: "new" | "follow_up" | "converted" | "resolved" | "spam";
  assignee_id: string | null;
  due_at: string | null;
  overdue: boolean;
  version: number;
  created_at: string;
}

/** CONTENT-02 blog post (packages/domain/content/src/blog.ts presentPost). */
export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  author_name: string;
  seo_title: string | null;
  seo_description: string | null;
  state: "draft" | "scheduled" | "published" | "archived";
  publish_at: string | null;
  published_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}
export type BlogInput = Pick<
  BlogPost,
  "slug" | "title" | "excerpt" | "body" | "author_name" | "seo_title" | "seo_description"
>;

export const sfApi = {
  store: (o: string, s: string) => request<StoreSummary>("GET", S(o, s)),
  settings: (o: string, s: string) => request<StoreSettings>("GET", `${S(o, s)}/settings`),
  patchSettings: (
    o: string,
    s: string,
    version: number,
    body: Partial<Omit<StoreSettings, "version" | "has_password">>,
  ) => request<StoreSettings>("PATCH", `${S(o, s)}/settings`, { body, ifMatch: version }),
  legal: (o: string, s: string) => request<LegalProfile>("GET", `${S(o, s)}/legal-profile`),
  putLegal: (o: string, s: string, version: number, body: Record<string, unknown>) =>
    request<LegalProfile>("PUT", `${S(o, s)}/legal-profile`, { body, ifMatch: version }),
  /** D-094: the celebration was shown; it never shows again for this shop. */
  celebrated: (o: string, s: string) =>
    request<void>("POST", `${S(o, s)}/celebrated`, { body: {} }),
  /** D-071: send every page of the live store to IndexNow. */
  indexNow: (o: string, s: string) =>
    request<{ submitted: number; at: string }>("POST", `${S(o, s)}/seo/indexnow`, { body: {} }),
  previewLink: (o: string, s: string) =>
    request<{ url: string; urls: string[]; expires_at: string }>(
      "POST",
      `${S(o, s)}/preview-links`,
      { body: {} },
    ),
  readiness: (o: string, s: string) =>
    request<{ items: ReadinessItem[]; done: number; total: number }>("GET", `${S(o, s)}/readiness`),
  domains: (o: string, s: string) =>
    request<{ data: StoreSummary["hosts"]; custom_domains: { state: string } }>(
      "GET",
      `${S(o, s)}/domains`,
    ),

  catalog: () => request<Page<CatalogTheme>>("GET", "/themes"),
  /** D-093: what the gallery and the theme preview draw for an uploaded theme (`has_preview`). */
  previewData: (key: string, version?: string) =>
    request<ThemePreviewData>(
      "GET",
      `/themes/${encodeURIComponent(key)}/preview-data${version ? `?v=${encodeURIComponent(version)}` : ""}`,
    ),
  themeFileUrl,
  draft: (o: string, s: string) => request<ThemeDraft>("GET", `${S(o, s)}/theme/draft`),
  install: (o: string, s: string, themeKey: string, idempotencyKey: string) =>
    request<ThemeDraft & { id: string }>("POST", `${S(o, s)}/theme/install`, {
      body: { theme_key: themeKey },
      idempotencyKey,
    }),
  /** Owner, 2026-11-01: "Apply" puts a theme live in one step, as shipped. */
  apply: (o: string, s: string, themeKey: string, idempotencyKey: string) =>
    request<ThemeDraft & { n: number; previous_n: number | null }>(
      "POST",
      `${S(o, s)}/theme/apply`,
      { body: { theme_key: themeKey }, idempotencyKey },
    ),
  saveDraft: (o: string, s: string, installId: string, version: number, document: ThemeDocument) =>
    request<ThemeDraft>("PUT", `${S(o, s)}/theme/draft`, {
      body: { install_id: installId, document },
      ifMatch: version,
    }),
  publish: (
    o: string,
    s: string,
    body: { install_id: string; label?: string },
    idempotencyKey: string,
  ) =>
    request<{ id: string; n: number; published_at: string; content_version: number }>(
      "POST",
      `${S(o, s)}/theme/publish`,
      {
        body,
        idempotencyKey,
      },
    ),
  versions: (o: string, s: string) =>
    request<Page<ThemeVersion>>("GET", `${S(o, s)}/theme/versions`),
  restore: (o: string, s: string, n: number) =>
    request<{ n: number }>("POST", `${S(o, s)}/theme/versions/${n}/restore`, { body: {} }),
  nameVersion: (o: string, s: string, n: number, label: string) =>
    request<{ n: number }>("POST", `${S(o, s)}/theme/versions/${n}/name`, { body: { label } }),

  pages: (o: string, s: string) => request<Page<StorePage>>("GET", `${S(o, s)}/pages`),
  createPage: (
    o: string,
    s: string,
    body: { title: string; slug: string; template: string },
    idempotencyKey: string,
  ) => request<StorePage>("POST", `${S(o, s)}/pages`, { body, idempotencyKey }),
  updatePage: (
    o: string,
    s: string,
    id: string,
    version: number,
    body: { title: string; seo: StorePage["seo"]; sections: SectionDoc[] },
  ) =>
    request<StorePage>("PUT", `${S(o, s)}/pages/${encodeURIComponent(id)}`, {
      body,
      ifMatch: version,
    }),
  publishPage: (o: string, s: string, id: string) =>
    request<StorePage>("POST", `${S(o, s)}/pages/${encodeURIComponent(id)}/publish`, { body: {} }),
  setHome: (o: string, s: string, id: string) =>
    request<StorePage>("POST", `${S(o, s)}/pages/${encodeURIComponent(id)}/set-home`, { body: {} }),
  menu: (o: string, s: string, key: "main" | "footer") =>
    request<{ key: string; items: MenuItem[]; version: number }>("GET", `${S(o, s)}/menus/${key}`),
  saveMenu: (o: string, s: string, key: "main" | "footer", version: number, items: MenuItem[]) =>
    request<{ key: string; items: MenuItem[]; version: number }>("PUT", `${S(o, s)}/menus/${key}`, {
      body: { items },
      ifMatch: version,
    }),

  content: (o: string, s: string, type: string) =>
    request<Page<ContentEntry>>("GET", `${S(o, s)}/content/${type}`),
  putContent: (
    o: string,
    s: string,
    type: string,
    key: string,
    version: number,
    body: {
      title: string;
      body: string;
      position?: number;
      reviewed?: boolean;
      data?: Record<string, unknown>;
    },
  ) =>
    request<ContentEntry>("PUT", `${S(o, s)}/content/${type}/${encodeURIComponent(key)}`, {
      body,
      ifMatch: version,
    }),
  deleteContent: (o: string, s: string, type: string, key: string) =>
    request<void>("DELETE", `${S(o, s)}/content/${type}/${encodeURIComponent(key)}`),
  installPolicies: (o: string, s: string, idempotencyKey: string) =>
    request<Page<ContentEntry>>("POST", `${S(o, s)}/content/policy/templates`, {
      body: {},
      idempotencyKey,
    }),

  posts: (o: string, s: string) => request<Page<BlogPost>>("GET", `${S(o, s)}/blog/posts`),
  createPost: (o: string, s: string, body: BlogInput, idempotencyKey: string) =>
    request<BlogPost>("POST", `${S(o, s)}/blog/posts`, { body, idempotencyKey }),
  updatePost: (o: string, s: string, p: BlogPost, body: Partial<BlogInput>) =>
    request<BlogPost>("PATCH", `${S(o, s)}/blog/posts/${encodeURIComponent(p.id)}`, {
      body,
      ifMatch: p.version,
    }),
  /** Publish now (publish_at null) or schedule for a time (ISO with offset). */
  publishPost: (o: string, s: string, p: BlogPost, publishAt: string | null) =>
    request<BlogPost>("POST", `${S(o, s)}/blog/posts/${encodeURIComponent(p.id)}/publish`, {
      body: { publish_at: publishAt },
      ifMatch: p.version,
    }),
  unpublishPost: (o: string, s: string, p: BlogPost) =>
    request<BlogPost>("POST", `${S(o, s)}/blog/posts/${encodeURIComponent(p.id)}/unpublish`, {
      body: {},
      ifMatch: p.version,
    }),

  inbox: (o: string, s: string, q: { kind?: string; pipeline?: string; cursor?: string }) =>
    request<Page<Submission>>("GET", `${S(o, s)}/inbox`, { query: q }),
  updateSubmission: (
    o: string,
    s: string,
    id: string,
    version: number,
    /** The status, who handles it (a user ID; null for no one), or both. */
    body: { pipeline?: Submission["pipeline"]; assignee_id?: string | null },
  ) =>
    request<Submission>("PATCH", `${S(o, s)}/inbox/${encodeURIComponent(id)}`, {
      body,
      ifMatch: version,
    }),
};

export const sfKeys = {
  store: (o: string, s: string) => ["sf", o, s, "store"] as const,
  settings: (o: string, s: string) => ["sf", o, s, "settings"] as const,
  legal: (o: string, s: string) => ["sf", o, s, "legal"] as const,
  readiness: (o: string, s: string) => ["sf", o, s, "readiness"] as const,
  domains: (o: string, s: string) => ["sf", o, s, "domains"] as const,
  catalog: ["sf", "themes"] as const,
  previewData: (key: string, version: string) => ["sf", "themes", key, version, "preview"] as const,
  draft: (o: string, s: string) => ["sf", o, s, "draft"] as const,
  versions: (o: string, s: string) => ["sf", o, s, "versions"] as const,
  pages: (o: string, s: string) => ["sf", o, s, "pages"] as const,
  menu: (o: string, s: string, k: string) => ["sf", o, s, "menu", k] as const,
  content: (o: string, s: string, type: string) => ["sf", o, s, "content", type] as const,
  inbox: (o: string, s: string, kind: string) => ["sf", o, s, "inbox", kind] as const,
  posts: (o: string, s: string) => ["sf", o, s, "posts"] as const,
};

export const useSf = <T>(key: readonly unknown[], fn: () => Promise<T>, enabled = true) =>
  useQuery({ queryKey: key, queryFn: fn, enabled });

/** The address shoppers open: the alias host when there is one (staging serves a store there), else the
 * canonical host. */
export const storeAddress = (s: Pick<StoreSummary, "hosts" | "canonical_host">) =>
  `https://${s.hosts.find((h) => h.serve_mode === "alias")?.hostname ?? s.canonical_host}/`;

/** WCAG contrast vs white (display hint only; the server enforces the AA gate on save). */
export function contrastWithWhite(hex: string): number {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return 0;
  const n = Number.parseInt(hex.slice(1), 16);
  const lum = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map((v) => v / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const l = 0.2126 * lum[0]! + 0.7152 * lum[1]! + 0.0722 * lum[2]!;
  return Math.round((1.05 / (l + 0.05)) * 100) / 100;
}
